import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Buildings,
  Storefront,
  Link as PhosphorLink,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  MagnifyingGlass,
  Envelope,
  Phone,
  Trash,
  Lightning,
} from '@phosphor-icons/react';
import { useAuthStore, useNotificationStore } from '@dukene/shared';
import { useSuppliers, useDeleteSupplier } from '../../hooks/useSuppliers';
import { confirmAction } from '../../components/modals/ConfirmModal';
import { marketplaceApi, OAuthAuthorization } from '@dukene/shared/api/marketplace';

export function SuppliersSubNav({
  activeTab,
  onTabChange,
}: {
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 border-b border-[#E1E4E8] dark:border-slate-800 pb-2 mb-4 overflow-x-auto">
      <button
        type="button"
        onClick={() => onTabChange && onTabChange('CUSTOM')}
        className={`px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition-colors whitespace-nowrap ${
          activeTab === 'CUSTOM' || !activeTab
            ? 'bg-[#006838] text-white shadow-xs'
            : 'text-[#5C5F62] dark:text-slate-400 hover:bg-[#F6F6F7] dark:hover:bg-slate-800'
        }`}
      >
        <Buildings weight="bold" className="w-3.5 h-3.5" />
        My Direct Suppliers
      </button>

      <Link
        to="/suppliers/connected"
        className="px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 text-[#5C5F62] dark:text-slate-400 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 transition-colors whitespace-nowrap"
      >
        <PhosphorLink weight="bold" className="w-3.5 h-3.5" />
        Connected Suppliers
      </Link>

      <Link
        to="/suppliers/marketplace"
        className="px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 text-[#5C5F62] dark:text-slate-400 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 transition-colors whitespace-nowrap"
      >
        <Storefront weight="bold" className="w-3.5 h-3.5" />
        B2B Marketplace
      </Link>
    </div>
  );
}

export default function Suppliers() {
  const { user } = useAuthStore();
  const storeId = user?.storeId || '';
  const [searchParams] = useSearchParams();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [activeTab, setActiveTab] = useState<'CUSTOM' | 'CONNECTED'>('CUSTOM');
  const [oauthStatuses, setOauthStatuses] = useState<Record<string, OAuthAuthorization>>({});

  const { data, isLoading } = useSuppliers(storeId, {
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
    search: search || undefined,
    page: 1,
    limit: 50,
  });

  const deleteSupplier = useDeleteSupplier();
  const suppliers = data?.suppliers || [];

  useEffect(() => {
    const oauthStatus = searchParams.get('oauth');
    const supplierName = searchParams.get('supplier');
    const { addNotification } = useNotificationStore.getState();

    if (oauthStatus === 'success' && supplierName) {
      addNotification({ type: 'success', message: `Successfully connected to ${supplierName}!` });
      window.history.replaceState({}, '', '/suppliers');
      setActiveTab('CONNECTED');
    } else if (oauthStatus === 'error') {
      addNotification({
        type: 'error',
        message: 'OAuth authorization failed. Please try again.',
      });
      window.history.replaceState({}, '', '/suppliers');
      setActiveTab('CONNECTED');
    }
  }, [searchParams]);

  useEffect(() => {
    const loadOAuthStatuses = async () => {
      if (activeTab === 'CONNECTED' && suppliers.length > 0 && storeId) {
        const statuses: Record<string, OAuthAuthorization> = {};
        for (const s of suppliers) {
          const mId = (s as any).marketplaceSupplierId || s.supplierId;
          if (mId) {
            try {
              const res = await marketplaceApi.getOAuthStatus(storeId, mId);
              statuses[s.id] = res.authorization;
            } catch {
              // ignore error
            }
          }
        }
        setOauthStatuses(statuses);
      }
    };

    loadOAuthStatuses();
  }, [activeTab, suppliers, storeId]);

  const handleDelete = async (id: string, name: string) => {
    const confirmed = await confirmAction({
      title: 'Remove Supplier',
      message: `Are you sure you want to remove supplier "${name}"?`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
    });
    if (confirmed) {
      deleteSupplier.mutate(id);
    }
  };

  const getStatusBadge = (status?: string) => {
    const s = (status || '').toUpperCase();
    if (s === 'APPROVED') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-[#E3F1DF] text-[#006838] border border-[#B3DEC1]">
          <CheckCircle weight="fill" className="w-3 h-3" />
          Approved
        </span>
      );
    }
    if (s === 'PENDING') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
          <Clock weight="fill" className="w-3 h-3" />
          Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
        <XCircle weight="fill" className="w-3 h-3" />
        {s || 'Inactive'}
      </span>
    );
  };

  const activeCount = suppliers.filter((s: any) => s.isActive).length;

  return (
    <div className="space-y-5">
      <SuppliersSubNav
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab as 'CUSTOM' | 'CONNECTED')}
      />

      {/* Polaris High-Density Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold text-[#202223] dark:text-white">
            Supplier Network
          </h1>
          <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
            Manage your B2B supplier directory, automate catalog syncing, and monitor purchase orders
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/suppliers/marketplace">
            <button className="h-8 px-3.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-[#F6F6F7] text-xs font-semibold text-[#202223] dark:text-white inline-flex items-center gap-1.5 transition-colors">
              <Storefront weight="bold" className="w-3.5 h-3.5 text-[#006838]" />
              Browse Marketplace
            </button>
          </Link>
          <Link to="/suppliers/new">
            <button className="h-8 px-4 bg-[#006838] hover:bg-[#00522c] text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 shadow-xs transition-colors">
              <Plus weight="bold" className="w-3.5 h-3.5" />
              Add Supplier
            </button>
          </Link>
        </div>
      </div>

      {/* KPI Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">
              Total Suppliers
            </span>
            <Buildings weight="fill" className="w-4 h-4 text-[#5C5F62]" />
          </div>
          <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-1">
            {suppliers.length}
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">
              Active Partners
            </span>
            <CheckCircle weight="fill" className="w-4 h-4 text-[#006838]" />
          </div>
          <p className="text-xl font-bold text-[#006838] font-mono mt-1">{activeCount}</p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">
              Connected API Syncs
            </span>
            <Lightning weight="fill" className="w-4 h-4 text-amber-600" />
          </div>
          <p className="text-xl font-bold text-amber-600 font-mono mt-1">
            {Object.keys(oauthStatuses).length}
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-3.5 border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/50 dark:bg-slate-900/40 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2 relative">
            <MagnifyingGlass
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F62]"
            />
            <input
              type="text"
              placeholder="Search supplier by name, email, or contact person..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-8 pl-9 pr-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white placeholder-[#8C9196] focus:outline-none focus:ring-1 focus:ring-[#006838]"
            />
          </div>

          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-8 w-full rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
            >
              <option value="ALL">All Statuses</option>
              <option value="APPROVED">Approved</option>
              <option value="PENDING">Pending</option>
              <option value="SUSPENDED">Suspended</option>
            </select>
          </div>
        </div>

        {/* Suppliers Directory Table */}
        {isLoading ? (
          <div className="p-12 space-y-3 animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-[#F6F6F7] dark:bg-slate-900 rounded-lg" />
            ))}
          </div>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Buildings weight="fill" className="w-10 h-10 text-[#8C9196] mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-[#202223] dark:text-white">
              No Suppliers Listed
            </h3>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1 max-w-sm mx-auto">
              Add your first direct supplier or discover verified partners in our B2B Marketplace.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/60 dark:bg-slate-900/40 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
                  <th className="py-2.5 px-4">Supplier Organization</th>
                  <th className="py-2.5 px-4">Contact Person</th>
                  <th className="py-2.5 px-4">Contact Info</th>
                  <th className="py-2.5 px-4">Status</th>
                  <th className="py-2.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800 text-xs">
                {suppliers.map((supplier: any) => (
                  <tr
                    key={supplier.id}
                    className="hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#F6F6F7] dark:bg-slate-800 flex items-center justify-center text-[#006838] font-bold text-xs shrink-0">
                          {supplier.name?.slice(0, 2).toUpperCase() || 'SP'}
                        </div>
                        <div>
                          <Link
                            to={`/suppliers/${supplier.id}`}
                            className="font-semibold text-[#202223] dark:text-white hover:text-[#006838]"
                          >
                            {supplier.name}
                          </Link>
                          {supplier.code && (
                            <span className="block text-[10px] text-[#5C5F62] font-mono mt-0.5">
                              Code: {supplier.code}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="py-3 px-4 text-[#202223] dark:text-slate-200">
                      {supplier.contactPerson || '—'}
                    </td>

                    <td className="py-3 px-4 text-[#5C5F62] dark:text-slate-400">
                      <div className="space-y-0.5">
                        {supplier.email && (
                          <div className="flex items-center gap-1.5">
                            <Envelope weight="bold" className="w-3.5 h-3.5 text-[#8C9196]" />
                            <span>{supplier.email}</span>
                          </div>
                        )}
                        {supplier.phone && (
                          <div className="flex items-center gap-1.5">
                            <Phone weight="bold" className="w-3.5 h-3.5 text-[#8C9196]" />
                            <span className="font-mono">{supplier.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="py-3 px-4">{getStatusBadge(supplier.status)}</td>

                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Link
                          to={`/suppliers/${supplier.id}`}
                          className="p-1.5 rounded hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#5C5F62] transition-colors"
                          title="View supplier details"
                        >
                          <Buildings weight="bold" className="w-4 h-4" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleDelete(supplier.id, supplier.name)}
                          className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/40 text-red-600 transition-colors"
                          title="Delete supplier"
                        >
                          <Trash weight="bold" className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
